import React, { useState, useRef, useEffect, useCallback } from 'react';
import { View, Text, TextInputProps } from 'react-native';
import { ErrorMessage, useField } from 'formik';
import { useColorScheme } from 'nativewind';
import { Editor, EditorProvider, Toolbar } from 'react-simple-wysiwyg';
import sanitizeHtml from 'sanitize-html';
import Icon from '../Icon';
import svgIcons from '../../assets';
import { cln } from '../../utils/classnames';
import { isWeb } from '../../utils/responsive';
import './style.css';

interface Props extends TextInputProps {
  title: string;
  name: string;
  required?: boolean;
  placeholder?: string;
  maxCharacter?: number;
}

const EditorInput: React.FC<Props> = ({
  title,
  name,
  required = false,
  placeholder,
  maxCharacter,
  ...props
}) => {
  const { colorScheme } = useColorScheme();
  const [field, meta, helpers] = useField(name);
  const containerRef = useRef(null);
  const contentEditableRef = useRef(null);
  const savedSelection = useRef(null);

  const [isFocused, setIsFocused] = useState<boolean>(false);

  const isDarkMode = colorScheme === 'dark';

  function editorChange(e) {
    const value = e.target.value.trim();
    helpers.setValue(value);
  }

  const updateContentEditableRef = useCallback(() => {
    if (containerRef.current) {
      const editable = containerRef.current.querySelector('[contenteditable="true"]');
      if (editable) {
        contentEditableRef.current = editable;
      }
    }
  }, []);

  useEffect(() => {
    updateContentEditableRef();
  }, [field.value, updateContentEditableRef]);

  useEffect(() => {
    const handleSelectionChange = () => {
      const sel = window.getSelection();
      if (
        sel.rangeCount > 0 &&
        contentEditableRef.current &&
        contentEditableRef.current.contains(sel.anchorNode)
      ) {
        savedSelection.current = sel.getRangeAt(0).cloneRange();
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  // Tag transformation results in unnecessary line breaks in the beginning of the text
  const leftTrimLineBreaks = (text) => {
    while (text.startsWith('<br />')) {
      text = text.substring(6);
    }
    while (text.startsWith('<br>')) {
      text = text.substring(4);
    }
    return text;
  };

  // Text copied from editors have unnecessary styling, and it does not match our UI.
  // It also results in many hidden characters eating up the character limit
  // The solution is to sanitize the html from the clipboard
  // Known issue, cursor position is moved to the end of the text

  const handleRichTextPaste = (e) => {
    e.preventDefault();

    let clipboardData = e.clipboardData.getData('text/html');

    if (!clipboardData) {
      const plainText = e.clipboardData.getData('text');
      clipboardData = plainText || '';
    }

    const sanitizeConfig = {
      // Only keep tags that are available in the editor
      allowedTags: ['a', 'br', 'i'],
      // Keep line breaks
      transformTags: {
        p: 'br',
        div: 'br',
      },
    };
    const sanitizedText = leftTrimLineBreaks(sanitizeHtml(clipboardData, sanitizeConfig));

    updateContentEditableRef();

    const editable = contentEditableRef.current;
    const range = savedSelection.current;

    if (!editable || !range) return;

    // Insert sanitized text directly at cursor position
    range.deleteContents();

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sanitizedText;

    // Insert the actual HTML nodes at the cursor position
    const fragment = document.createDocumentFragment();
    Array.from(tempDiv.childNodes).forEach((node) => fragment.appendChild(node));

    range.insertNode(fragment);

    // Sync React state immediately with new HTML content
    helpers.setValue(editable.innerHTML);
  };

  const containerStyle: React.CSSProperties = {
    backgroundColor: isFocused
      ? isDarkMode
        ? '#f6f6f6'
        : '#101010'
      : isDarkMode
        ? '#101010'
        : '#f6f6f6',
    color: isFocused ? (isDarkMode ? '#101010' : '#f6f6f6') : isDarkMode ? '#f6f6f6' : '#101010',
    fontFamily: 'sans-400',
    flex: 1,
    borderColor: meta.touched && meta.error ? '#ff4d4d' : isDarkMode ? '#d1d1d1' : '#000000',
    borderWidth: '1px',
    borderRadius: 'revert',
    minHeight: '160px',
    maxHeight: '160px',
    overflow: 'auto',
    fontSize: '14px',
    lineHeight: 'normal',
  };

  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (field.value.trim() === '<br>') {
      helpers.setValue('');
    }
  }, [field.value]);

  return isWeb ? (
    <View className="mb-11 relative">
      <View className="flex-row">
        <Text className="text-input text-neutral-950 dark:text-neutral-50 font-[sans-600]">
          {title}
        </Text>
        {required && <Text className="text-input text-danger-500 font-[sans-400]"> *</Text>}
      </View>
      <View className="relative">
        <div ref={editorRef} className={cln(isDarkMode && 'dark-mode', isFocused && 'is-focused')}>
          <EditorProvider>
            <Editor
              value={field.value ? field.value : null}
              onChange={editorChange}
              containerProps={{
                ref: containerRef,
                style: containerStyle,
                onPaste: handleRichTextPaste,
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onSubmit={() => setIsFocused(false)}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              placeholder={placeholder}
            >
              <Toolbar
                style={{
                  backgroundColor: isDarkMode ? '#f6f6f6' : '#101010',
                  color: isDarkMode ? '#101010' : '#f6f6f6',
                  borderColor: isDarkMode ? '#101010' : '#f6f6f6',
                  position: 'sticky',
                  top: 0,
                }}
              >
                <button
                  style={{ border: 'none', backgroundColor: 'transparent' }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => document.execCommand('bold', false, '')}
                >
                  <Icon
                    icon={svgIcons.boldIcon}
                    mobileSize={{ width: 18, height: 18 }}
                    webSize={{ width: 18, height: 18 }}
                    classNames=""
                    inverted={true}
                  />
                </button>
                <button
                  style={{ border: 'none', backgroundColor: 'transparent' }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => document.execCommand('italic', false, '')}
                >
                  <Icon
                    icon={svgIcons.italicIcon}
                    mobileSize={{ width: 24, height: 24 }}
                    webSize={{ width: 24, height: 24 }}
                    classNames=""
                    inverted={true}
                  />
                </button>
                <button
                  style={{ border: 'none', backgroundColor: 'transparent' }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const url = prompt('Enter the URL');
                    const text = window.getSelection()?.toString() || 'Click here';
                    if (url) {
                      document.execCommand(
                        'insertHTML',
                        false,
                        `<a href="${url}" target="${url.includes(process.env.EXPO_PUBLIC_APP_DOMAIN) ? '_self' : '_blank'}">${text}</a>`,
                      );
                    }
                  }}
                >
                  <Icon
                    icon={svgIcons.linkChainIcon}
                    mobileSize={{ width: 24, height: 24 }}
                    webSize={{ width: 20, height: 20 }}
                    classNames=""
                    inverted={true}
                  />
                </button>
              </Toolbar>
            </Editor>
          </EditorProvider>
        </div>
      </View>
      <ErrorMessage name={name} className={'pt-10'}>
        {(message) => (
          <Text
            className="text-notificationMessage text-danger-500 font-[sans-600] absolute"
            style={{ top: 187 }}
          >
            {message}
          </Text>
        )}
      </ErrorMessage>
    </View>
  ) : (
    <> </>
  );
};

export default EditorInput;
